import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import type { ApplicationConfig, Provider, Type } from '@angular/core';
import { inject, Injector, isDevMode, LOCALE_ID, provideZonelessChangeDetection } from '@angular/core';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS } from '@angular/material/progress-spinner';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideClientHydration, withEventReplay, withNoIncrementalHydration } from '@angular/platform-browser';
import { provideRouter, TitleStrategy, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { errorInterceptor } from '@app/core/errors/error.interceptor';
import { provideGlobalErrorHandler } from '@app/core/errors/global-error-handler';
import { getPtBrPaginatorIntl } from '@app/core/locale/paginator-ptbr.util';
import { provideLeadGenMask } from '@app/core/locale/provide-lead-gen-mask';
import { pendingRequestsInterceptor } from '@app/core/progress/pending-requests.interceptor';
import { RealtimeConnectionService } from '@app/core/realtime/realtime-connection.service';
import { authInterceptor } from '@app/core/session/auth.interceptor';
import { SESSION_CLEANUP } from '@app/core/session/session-store';
import { PageTitleStrategy } from '@app/core/shell/page-title.strategy';
import { SentimentReferenceService } from '@shared/api/conversations/references/sentiment-reference.service';
import { UserReferenceService } from '@shared/api/identity/references/user-reference.service';
import { GenderReferenceService } from '@shared/api/leads/references/gender-reference.service';
import { routes } from './app.routes';

registerLocaleData(localePt);

function clearOnSignOut(reference: Type<{ clear(): void }>): Provider {
  return {
    provide: SESSION_CLEANUP,
    multi: true,
    useFactory: () => {
      const injector = inject(Injector);

      return () => injector.get(reference).clear();
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideClientHydration(withNoIncrementalHydration(), withEventReplay()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideHttpClient(withInterceptors([authInterceptor, pendingRequestsInterceptor, errorInterceptor])),
    provideGlobalErrorHandler(),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-symbols-outlined' } },
    { provide: MatPaginatorIntl, useFactory: getPtBrPaginatorIntl },
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { ...new MatDialogConfig(), width: 'var(--modal-width-md)', maxWidth: '92vw' },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { horizontalPosition: 'end', verticalPosition: 'top' },
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: { hidePageSize: true, showFirstLastButtons: true },
    },
    { provide: MAT_CARD_CONFIG, useValue: { appearance: 'outlined' } },
    { provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS, useValue: { diameter: 20 } },
    provideLeadGenMask(),
    clearOnSignOut(GenderReferenceService),
    clearOnSignOut(SentimentReferenceService),
    clearOnSignOut(UserReferenceService),
    clearOnSignOut(RealtimeConnectionService),
  ],
};
